import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { apiFetch } from '../api/client';
import type { SyncResult, SyncStatus } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { compareUrl } from '../lib/github';
import { useViewBranch } from '../view/ViewBranchContext';

export const UserMenu = () => {
  const { state, signOutCurrentUser } = useAuth();
  const { viewBranch, isReadOnly } = useViewBranch();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [conflict, setConflict] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const { data: sync } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => apiFetch<SyncStatus>('/api/sync'),
    enabled: state.status === 'signed-in',
    refetchInterval: 30 * 60 * 1000,
  });

  const syncMutation = useMutation({
    mutationFn: () => apiFetch<SyncResult>('/api/sync', { method: 'POST' }),
    onSuccess: (res) => {
      if (res.status === 'conflict') {
        setConflict(true);
        return;
      }
      setConflict(false);
      void queryClient.invalidateQueries({ queryKey: ['group'] });
      void queryClient.invalidateQueries({ queryKey: ['sync-status'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node) === false) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (state.status !== 'signed-in') return null;

  const mainAhead = sync?.mainAhead ?? 0;
  const branch = sync?.branch ?? null;
  const canSync = !syncMutation.isPending && mainAhead > 0;
  // 差分リンクは閲覧中ブランチ (他者) を優先。自分の目線では自分のブランチを指す。
  const compareBranch = viewBranch ?? branch;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="メニューを開く"
        aria-expanded={open}
        className="flex h-8 w-8 items-center justify-center rounded border text-gray-700 hover:bg-gray-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full right-0 z-40 mt-1 w-64 rounded border bg-white p-2 text-sm shadow-lg">
          <div className="border-b px-2 py-1.5 text-xs text-gray-600">{state.email}</div>
          {/* main の取り込みは自分のブランチへの書き込み。他者ブランチ閲覧中は隠す。 */}
          {!isReadOnly && (
            <button
              type="button"
              onClick={() => syncMutation.mutate()}
              disabled={!canSync}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-400 disabled:hover:bg-transparent"
            >
              <span>
                {syncMutation.isPending
                  ? '取り込み中...'
                  : mainAhead === 0
                    ? 'main は最新です'
                    : `main を取り込む (+${mainAhead.toString()})`}
              </span>
            </button>
          )}
          {!isReadOnly && conflict && branch !== null && (
            <div className="px-2 py-1 text-xs text-red-700">
              コンフリクトが発生しました。GitHub Web で {branch} を main に追従させてください。
            </div>
          )}
          {compareBranch !== null && (
            <a
              href={compareUrl(compareBranch)}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="block rounded px-2 py-1.5 text-gray-800 hover:bg-gray-50"
            >
              GitHub で差分を見る
            </a>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              signOutCurrentUser().catch((e: unknown) => {
                console.error('Sign out failed:', e);
              });
            }}
            className="block w-full rounded px-2 py-1.5 text-left text-gray-800 hover:bg-gray-50"
          >
            ログアウト
          </button>
        </div>
      )}
    </div>
  );
};
